import { useContext, useEffect, useRef, useState } from 'react'
import { useForm } from '@inertiajs/react'
import Modal from '../shared/Modal'
import MeetingTopicActions, { MeetingCreationContext } from './MeetingTopicActions'
import PriorityDropdown from '../topics/PriorityDropdown'
import CategoryDropdown from '../topics/CategoryDropdown'
import type { User } from '../../types'
import { sortPeople } from '../../utils/peopleSort'

/** Polymorphic record a topic is being raised from (today: a Scoreboard metric). */
export interface TopicSource {
  type: string
  id: number
}

export interface TopicCreatePayload {
  topic: Record<string, unknown>
}

export interface TopicSubmitHandlers {
  onSuccess: () => void
  /** `message` is the joined server error, or '' when none was returned. */
  onError: (message: string) => void
}

interface TopicModalProps {
  isOpen: boolean
  onClose: () => void
  meetingId: number
  teamMembers: User[]
  currentUserId?: number
  /** Pre-fills the Topic field. Omit for a blank topic. */
  initialTitle?: string
  /**
   * Pre-fills "Assigned to (Owner)". Pass '' to deliberately leave the owner
   * unset (e.g. the metric is unassigned); omit to keep the default of the
   * current user.
   */
  initialOwnerId?: number | ''
  /**
   * Record the topic is raised from. Sent with the FIRST create only — see
   * `submitCreate`.
   */
  source?: TopicSource | null
  /**
   * Optional external submitter. The Scoreboard passes one so the create goes
   * through its serialized save queue instead of firing an Inertia visit of
   * its own, which would cancel a cell autosave still in flight.
   */
  onSubmit?: (payload: TopicCreatePayload, handlers: TopicSubmitHandlers) => void
}

export default function TopicModal({
  isOpen,
  onClose,
  meetingId,
  teamMembers,
  currentUserId,
  initialTitle,
  initialOwnerId,
  source = null,
  onSubmit,
}: TopicModalProps) {
  const meetingContext = useContext(MeetingCreationContext)
  const titleInputRef = useRef<HTMLInputElement>(null)
  const defaultOwnerId =
    currentUserId && teamMembers.some(m => m.id === currentUserId)
      ? currentUserId
      : (teamMembers[0]?.id ?? '')

  const { data, setData, post, processing, reset, errors: rawErrors } = useForm({
    topic: {
      title: '',
      category: '',
      priority: false,
      owner_id: defaultOwnerId as number | '',
      topic_type: 'short_term',
    },
  })
  const errors = rawErrors as Record<string, string>

  // The source travels in local state, not in the form data, because it is
  // dropped after the first successful save (see `submitCreate`).
  const [pendingSource, setPendingSource] = useState<TopicSource | null>(null)
  // Errors from the external submitter, which bypasses useForm's error bag.
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [externalProcessing, setExternalProcessing] = useState(false)

  // Seed the form on the closed→open transition only, keyed by the record the
  // modal was opened for. The live meeting page re-renders on every heartbeat
  // and callers rebuild the `source`/member props each render, so an effect
  // keyed on prop identity would wipe out what the user has typed.
  const seedKey = source ? `${source.type}:${source.id}` : 'blank'
  const seededForRef = useRef<string | null>(null)
  useEffect(() => {
    if (!isOpen) {
      seededForRef.current = null
      return
    }
    if (seededForRef.current === seedKey) return
    seededForRef.current = seedKey

    setData('topic', {
      title: initialTitle ?? '',
      category: '',
      priority: false,
      owner_id: initialOwnerId !== undefined ? initialOwnerId : defaultOwnerId,
      topic_type: 'short_term',
    })
    setPendingSource(source)
    setSubmitError(null)
    setExternalProcessing(false)
    // Intentionally keyed on the open transition + record only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, seedKey])

  const updateField = (field: string, value: any) => {
    setData('topic', { ...data.topic, [field]: value })
  }

  const submitCreate = (createAnother: boolean) => {
    const afterSuccess = () => {
      // Only the reviewed, pre-filled topic is genuinely raised from the
      // source record; anything typed afterwards is a plain new topic.
      setPendingSource(null)
      if (createAnother) {
        setData('topic', { ...data.topic, title: '' })
        titleInputRef.current?.focus()
      } else {
        reset()
        onClose()
      }
    }

    if (onSubmit) {
      const payload: TopicCreatePayload = {
        topic: {
          ...data.topic,
          ...(pendingSource ? { source_type: pendingSource.type, source_id: pendingSource.id } : {}),
        },
      }
      setSubmitError(null)
      setExternalProcessing(true)
      onSubmit(payload, {
        onSuccess: () => {
          setExternalProcessing(false)
          afterSuccess()
        },
        onError: (message) => {
          // Stay open with everything the user entered still in place — a
          // rejected save must never look like a successful one.
          setExternalProcessing(false)
          setSubmitError(message || 'The topic could not be saved. Please try again.')
        },
      })
      return
    }

    post(`/meetings/${meetingId}/topics`, {
      preserveScroll: true,
      onSuccess: afterSuccess,
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    submitCreate(false)
  }

  const handleClose = () => {
    reset()
    setPendingSource(null)
    setSubmitError(null)
    onClose()
  }

  const busy = processing || externalProcessing

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add to The List" subtleBackdrop={!!meetingContext}
      headerActions={<MeetingTopicActions onSubmit={onSubmit} />}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {submitError && (
          <p role="alert" className="px-3 py-2 rounded-lg bg-[#ED1C24]/10 text-[#ED1C24]" style={{ fontSize: '14px' }}>
            {submitError}
          </p>
        )}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1 font-sans" style={{ fontSize: '14px' }}>
            Topic <span className="text-[#ED1C24]">*</span>
          </label>
          <input
            ref={titleInputRef}
            type="text"
            value={data.topic.title}
            onChange={e => updateField('title', e.target.value)}
            required
            className="w-full px-3 py-2 border border-[#351E1D]/20 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-[#ED1C24]/20 focus:border-[#ED1C24]"
            style={{ fontSize: '16px' }}
            placeholder="What needs to be discussed?"
          />
          {(errors['topic.title'] || errors.title) && <p role="alert" className="text-[#ED1C24] mt-1" style={{ fontSize: '14px' }}>{errors['topic.title'] || errors.title}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1 font-sans" style={{ fontSize: '14px' }}>
            Assigned to (Owner)
          </label>
          <select
            value={data.topic.owner_id}
            onChange={e => updateField('owner_id', e.target.value ? Number(e.target.value) : '')}
            className="w-full px-3 py-2 border border-[#351E1D]/20 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-[#ED1C24]/20 focus:border-[#ED1C24]"
            style={{ fontSize: '16px' }}
          >
            <option value="">Select owner</option>
            {sortPeople(teamMembers).map(m => (
              <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1 font-sans" style={{ fontSize: '14px' }}>
            Topic Type
          </label>
          <select
            value={data.topic.topic_type}
            onChange={e => updateField('topic_type', e.target.value)}
            className="w-full px-3 py-2 border border-[#351E1D]/20 rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-[#ED1C24]/20 focus:border-[#ED1C24]"
            style={{ fontSize: '16px' }}
          >
            <option value="short_term">Short Term</option>
            <option value="long_term">Camp</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1 font-sans" style={{ fontSize: '14px' }}>
            Priority
          </label>
          <PriorityDropdown
            value={data.topic.priority}
            onChange={p => updateField('priority', p)}
            expanded
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1 font-sans" style={{ fontSize: '14px' }}>
            Category
          </label>
          <CategoryDropdown
            value={data.topic.category}
            onChange={c => updateField('category', c)}
          />
        </div>

        <div className="flex flex-wrap justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 font-semibold text-text-primary hover:bg-[#351E1D]/5 rounded-lg transition-colors font-sans"
            style={{ fontSize: '16px' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => submitCreate(true)}
            disabled={busy}
            className="px-4 py-2 font-semibold text-[#351E1D] border border-[#351E1D]/20 hover:bg-[#351E1D]/5 rounded-lg transition-colors disabled:opacity-50 font-sans"
            style={{ fontSize: '16px' }}
          >
            {busy ? 'Adding...' : 'Save and Create Another'}
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-4 py-2 font-semibold text-white bg-[#ED1C24] hover:bg-pinnacle-red-dark rounded-lg transition-colors disabled:opacity-50 font-sans"
            style={{ fontSize: '16px' }}
          >
            {busy ? 'Adding...' : 'Add Topic'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
